// The bodies of the four beasts of the old charts (R525), as flat point
// loops that two consumers read.
//
// `fast/gfx/atlas.ts` fills each loop set once into a baked cell — the mass
// and, in a second cell, the engraved hatching inside it. `fast/layers/
// kraken.ts` walks the SAME loops every frame and strokes the rim as a run
// of capsules, so the sonar light runs around the edge instead of lifting
// the whole outline at one alpha. One curve, two consumers, no drift.
//
// The paths are the mockup's own (`archive/mockups/027-kraken-chart.html`,
// R523): `domePath` for the three side-on bodies of cards 01, 02 and 06,
// and card 07's finned mantle with its head. They are the drawing, so the
// bezier coefficients stay literals here and the data layer owns the one
// figure a reader tunes: how long the beast is (`presentation.creatures`).
// The loops are therefore written in CARD units, and `cardLen` says what
// length the TOML figure scales against.
//
// Card 02 leans its body and cuts it at the waterline. A clip has no batch
// primitive, so the lean, the shift and the cut are baked INTO the loop:
// the breach body is the only one whose points are already in the beast's
// own stage frame, and its foot is the straight wet line.

import { cubic, quadratic } from './marks';

/** How many points one cubic segment of an outline is sampled into. */
const CUBIC_STEPS = 8;
/** The same for one quadratic segment, which is always the shorter foot. */
const QUAD_STEPS = 4;
/** An ellipse loop — card 07's head is the only one. */
const ELLIPSE_STEPS = 14;

/** Where a beast's engraving strokes run. `hatch` of the mockup's kit. */
export interface HatchPlan {
  /** The middle of the stroke fan, in card units. */
  cx: number;
  cy: number;
  /** How far the fan reaches from that middle, on both axes. */
  span: number;
  /** The gap between two strokes. */
  gap: number;
  /** The angle every stroke runs at, in radians. */
  ang: number;
}

/** One beast's body: what the atlas bakes and what the layer strokes. */
export interface KrakenBody {
  /** `hood`, `breach`, `dome` or `keel`. The atlas keys are built from it. */
  key: string;
  /** Closed loops of flat x, y pairs, in card units. */
  loops: readonly Float32Array[];
  hatch: HatchPlan;
  /** The ink bounds of every loop, which BOTH cells are measured by. */
  box: { x: number; y: number; w: number; h: number };
  /** The card length the data layer's figure for this beast scales against. */
  cardLen: number;
}

// ---------------------------------------------------------------------------
// The two curves
// ---------------------------------------------------------------------------
//
// `cubic` and `quadratic` of `marks.ts` sample each segment; the step counts
// above are this drawing's own.

/**
 * The bulbous mantle of the mockup's `domePath`: widest below the middle, a
 * crest that `crest` sharpens from a dome (0) to a point (1), and `lean`
 * pushing that crest sideways so the head has a direction.
 *
 * The returned loop is closed by construction: the quadratic foot ends on
 * the point the loop opened at, so the caller drops that last repeat.
 */
export function domeLoop(
  w: number, top: number, base: number, lean: number, crest: number,
): Float32Array {
  const h = base - top;
  const cx = lean * w;
  const k = crest;
  const out: number[] = [-w, base];
  cubic(out, -w, base,
    -w * 1.16, base - h * 0.48,
    -w * (0.92 - 0.55 * k), top + h * 0.06 * (1 - k),
    cx, top, CUBIC_STEPS);
  cubic(out, cx, top,
    w * (0.92 - 0.55 * k), top + h * 0.06 * (1 - k),
    w * 1.16, base - h * 0.48,
    w, base, CUBIC_STEPS);
  quadratic(out, w, base, 0, base + w * 0.34, -w, base, QUAD_STEPS);
  // The foot returned to the opening point; a closed loop states it once.
  out.length -= 2;
  return Float32Array.from(out);
}

/** An ellipse as a closed loop. Card 07's head is the only one. */
export function ellipseLoop(cx: number, cy: number, rx: number, ry: number): Float32Array {
  const out = new Float32Array(ELLIPSE_STEPS * 2);
  for (let k = 0; k < ELLIPSE_STEPS; k++) {
    const a = (k / ELLIPSE_STEPS) * Math.PI * 2;
    out[k * 2] = cx + Math.cos(a) * rx;
    out[k * 2 + 1] = cy + Math.sin(a) * ry;
  }
  return out;
}

/**
 * Turns and shifts a loop, in that order.
 *
 * Card 02 draws its body inside `translate(-8, 0); rotate(-0.20)`, so a
 * point of the body frame reaches the stage as `R·p + t`. The breach loop
 * is baked in the stage frame, because its cut is a stage-frame line.
 */
export function placeLoop(src: Float32Array, ang: number, tx: number, ty: number): Float32Array {
  const ca = Math.cos(ang), sa = Math.sin(ang);
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i += 2) {
    const x = src[i], y = src[i + 1];
    out[i] = ca * x - sa * y + tx;
    out[i + 1] = sa * x + ca * y + ty;
  }
  return out;
}

/**
 * Cuts a closed loop at a horizontal line and keeps the half above it.
 *
 * One half-plane of Sutherland and Hodgman: a point above the line is kept,
 * and every edge that crosses the line contributes the crossing point. The
 * result is the breach body, whose foot is the flat wet line the sonar
 * lights hardest.
 */
export function clipLoopBelow(src: Float32Array, y: number): Float32Array {
  const out: number[] = [];
  const n = src.length / 2;
  for (let i = 0; i < n; i++) {
    const ax = src[i * 2], ay = src[i * 2 + 1];
    const j = (i + 1) % n;
    const bx = src[j * 2], by = src[j * 2 + 1];
    const aIn = ay <= y, bIn = by <= y;
    if (aIn) out.push(ax, ay);
    if (aIn !== bIn) {
      const u = (y - ay) / (by - ay);
      out.push(ax + (bx - ax) * u, y);
    }
  }
  return Float32Array.from(out);
}

/** The ink bounds of a loop set, grown by `pad` on every side. */
export function loopsBox(
  loops: readonly Float32Array[], pad: number,
): { x: number; y: number; w: number; h: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const l of loops) {
    for (let i = 0; i < l.length; i += 2) {
      if (l[i] < minX) minX = l[i];
      if (l[i] > maxX) maxX = l[i];
      if (l[i + 1] < minY) minY = l[i + 1];
      if (l[i + 1] > maxY) maxY = l[i + 1];
    }
  }
  return {
    x: minX - pad, y: minY - pad,
    w: maxX - minX + pad * 2, h: maxY - minY + pad * 2,
  };
}

// ---------------------------------------------------------------------------
// The four bodies
// ---------------------------------------------------------------------------

/**
 * How far past the ink the baked box reaches.
 *
 * The mass cell is stroked by nothing, but the hatch cell must hold every
 * stroke end, and both cells must be measured by ONE box or the two sprites
 * would not sit on one another.
 */
const BOX_PAD = 3;

/** Card 01's waterline, in card units. The hood rises out of it. */
export const HOOD_WATERLINE = 30;
/** Card 02's waterline: its body is cut here and its rings sit on it. */
export const BREACH_WATERLINE = 18;
/** Card 02's lean and shift, which are baked into the breach loop. */
const BREACH_TILT = -0.2;
const BREACH_SHIFT = -8;

function body(
  key: string, loops: readonly Float32Array[], hatch: HatchPlan, cardLen: number,
): KrakenBody {
  return { key, loops, hatch, box: loopsBox(loops, BOX_PAD), cardLen };
}

/** Card 01: the hooded mantle that rises out of a drawn waterline. */
const HOOD = body('hood',
  [domeLoop(36, -82, HOOD_WATERLINE + 6, 0.08, 0.55)],
  { cx: 0, cy: -24, span: 84, gap: 5.2, ang: -0.42 },
  118);

/** Card 02: the leaning dome, cut at the surface. */
const BREACH = body('breach',
  [clipLoopBelow(
    placeLoop(domeLoop(42, -66, BREACH_WATERLINE + 30, -0.1, 0.3),
      BREACH_TILT, BREACH_SHIFT, 0),
    BREACH_WATERLINE)],
  // The body is baked turned, so the engraving turns with it.
  { cx: 0, cy: -18, span: 80, gap: 5.4, ang: 0.5 + BREACH_TILT },
  114);

/** Card 06: the dome the blur sharpens into. */
const DOME = body('dome',
  [domeLoop(32, -58, 26, 0, 0.12)],
  { cx: 0, cy: -16, span: 64, gap: 5, ang: -0.35 },
  84);

/** Card 07's mantle, its two fins and its head, as one loop set. */
function keelLoops(): Float32Array[] {
  const hx = -26;
  const mantle: number[] = [hx + 8, -26];
  cubic(mantle, hx + 8, -26, 28, -34, 74, -18, 96, 0, CUBIC_STEPS);
  cubic(mantle, 96, 0, 74, 18, 28, 34, hx + 8, 26, CUBIC_STEPS);
  const fins = [-1, 1].map((s) => {
    const out: number[] = [46, s * 12];
    quadratic(out, 46, s * 12, 70, s * 34, 88, s * 4, 6);
    return Float32Array.from(out);
  });
  return [Float32Array.from(mantle), ...fins, ellipseLoop(hx, 0, 30, 26)];
}

/** Card 07: the finned shadow that passes under the point. */
const KEEL = body('keel', keelLoops(),
  { cx: 20, cy: 0, span: 70, gap: 5.4, ang: 0.3 },
  // The mantle's nose to the back of the head: what the TOML figure names.
  152);

/**
 * The four bodies, in the order the beast index numbers them: card 01,
 * card 02, card 06 and card 07 (R525).
 */
export const KRAKEN_BODIES: readonly KrakenBody[] = [HOOD, BREACH, DOME, KEEL];

/** One body by its key, or a failure that names the key it could not find. */
export function krakenBody(key: string): KrakenBody {
  const b = KRAKEN_BODIES.find((x) => x.key === key);
  if (!b) throw new Error(`no kraken body ${key}`);
  return b;
}
